// src/pages/lms/LMSStudents.jsx
// UPDATED: Roll number system — admin can set/update roll_no for any student
// Auto-links all LMS records when roll_no is set
// Shows roll number in table, student detail panel, with quick set option
import { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import { Search, X, RefreshCw, Flag, ChevronRight, Badge } from 'lucide-react';

const INP = {
  width:'100%', padding:'9px 12px', borderRadius:8,
  border:'1.5px solid #E5E7EB', fontSize:13,
  boxSizing:'border-box', outline:'none', color:'#111827',
};

// ── Set Roll Number Modal ──────────────────────────────
function SetRollModal({ student, institutionId, onSave, onClose }) {
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const rollRef = useRef();

  const save = async () => {
    const roll = rollRef.current?.value?.trim() || '';
    setSaving(true); setError('');
    try {
      // Check duplicate
      if (roll) {
        const { data: existing } = await supabase
          .from('profiles').select('id,full_name')
          .eq('institution_id', institutionId)
          .eq('roll_no', roll).eq('role','student')
          .neq('id', student.id).maybeSingle();
        if (existing) {
          setError(`Roll number already assigned to ${existing.full_name}`);
          setSaving(false); return;
        }
      }
      // Update roll_no (trigger auto-links everything)
      const { error: e } = await supabase.from('profiles')
        .update({ roll_no: roll || null })
        .eq('id', student.id);
      if (e) throw e;
      onSave(roll
        ? `✅ Roll number ${roll} assigned to ${student.full_name}. All LMS records auto-linked!`
        : `Roll number cleared for ${student.full_name}`);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:420,padding:24}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <h3 style={{margin:0,fontWeight:800,fontSize:17,color:'#111827'}}>Set Roll Number</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={20} color="#6B7280"/></button>
        </div>

        {/* Student badge */}
        <div style={{background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)',border:'1px solid #BFDBFE',borderRadius:12,padding:14,marginBottom:16}}>
          <div style={{fontWeight:800,color:'#111827',fontSize:15}}>{student.full_name}</div>
          <div style={{fontSize:12,color:'#6B7280',marginTop:2}}>
            {student.degree && `${student.degree}${student.year_of_study ? ` · Year ${student.year_of_study}` : ''}`}
            {student.username && ` · @${student.username}`}
          </div>
          {student.roll_no && (
            <div style={{marginTop:6,fontSize:12,fontWeight:700,color:'#4F8EF7'}}>
              Current Roll#: {student.roll_no}
            </div>
          )}
        </div>

        {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px',marginBottom:12,fontSize:13,color:'#991B1B',fontWeight:600}}>❌ {error}</div>}

        <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:6}}>
          Roll Number (leave blank to clear)
        </label>
        <input ref={rollRef} defaultValue={student.roll_no||''} placeholder="e.g. 2024-CS-001 or F22BSCS001"
          style={INP} onKeyDown={e=>e.key==='Enter'&&save()}/>

        <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:8,padding:'10px 12px',margin:'12px 0',fontSize:12,color:'#065F46'}}>
          💡 Setting a roll number automatically links this student's class enrollment, attendance, fee vouchers, and library records.
        </div>

        <div style={{display:'flex',gap:10,marginTop:4}}>
          <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
            {saving ? 'Saving...' : '🔗 Assign Roll Number'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Flag Modal ─────────────────────────────────────────
function FlagModal({ student, onSave, onClose }) {
  const reasonRef = useRef();
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await supabase.from('profiles').update({
        account_status: student.account_status === 'flagged' ? 'active' : 'flagged',
        flag_reason: student.account_status === 'flagged' ? null : (reasonRef.current?.value?.trim()||'Flagged by admin'),
      }).eq('id', student.id);
      onSave(student.account_status === 'flagged' ? 'Student unflagged' : 'Student flagged');
    } catch(e) { setSaving(false); }
  };

  const isFlagged = student.account_status === 'flagged';
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:400,padding:24}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
          <h3 style={{margin:0,fontWeight:800,fontSize:17,color:'#111827'}}>{isFlagged ? 'Unflag' : 'Flag'} Student</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={20} color="#6B7280"/></button>
        </div>
        <div style={{fontWeight:700,color:'#111827',marginBottom:12}}>{student.full_name}</div>
        {!isFlagged && (
          <>
            <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:6}}>Reason</label>
            <input ref={reasonRef} defaultValue="" placeholder="Reason for flagging..." style={INP}/>
            <div style={{height:12}}/>
          </>
        )}
        <div style={{display:'flex',gap:10}}>
          <button onClick={onClose} style={{flex:1,padding:'11px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{flex:2,padding:'11px 0',borderRadius:10,border:'none',background:isFlagged?'#10B981':'#EF4444',color:'#fff',fontWeight:700,cursor:'pointer'}}>
            {saving ? '...' : isFlagged ? 'Unflag Student' : 'Flag Student'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────
export default function LMSStudents() {
  const { institution } = useLMSAuth();
  const [students,  setStudents]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [success,   setSuccess]   = useState('');
  const [selected,  setSelected]  = useState(null);
  const [showRoll,  setShowRoll]  = useState(false);
  const [showFlag,  setShowFlag]  = useState(false);
  const [rollStudent, setRollStudent] = useState(null);
  const [flagStudent, setFlagStudent] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search,    setSearch]    = useState('');
  const [noRollOnly,setNoRollOnly]= useState(false);
  const timer = useRef();

  useEffect(() => { if (institution?.id) load(); }, [institution?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.from('profiles')
        .select('id,full_name,username,email,roll_no,degree,year_of_study,created_at,account_status,phone,bio,city')
        .eq('institution_id', institution.id)
        .eq('role', 'student')
        .order('roll_no', { ascending:true, nullsFirst:false });

      // Get class info for each student
      const { data: clsData } = await supabase
        .from('lms_class_students')
        .select('student_id,student_roll_number,class_id,lms_classes(name)')
        .eq('institution_id', institution.id)
        .eq('enrollment_status','active');

      const clsMap = {};
      for (const c of (clsData||[])) {
        if (c.student_id)           clsMap[c.student_id]            = c.lms_classes?.name||'';
        if (c.student_roll_number)  clsMap[c.student_roll_number]   = c.lms_classes?.name||'';
      }

      setStudents((data||[]).map(s => ({
        ...s,
        class_name: clsMap[s.id] || clsMap[s.roll_no] || '',
      })));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const hs = v => {
    setSearchInput(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSearch(v), 200);
  };

  const filtered = useMemo(() => students.filter(s => {
    if (noRollOnly && s.roll_no) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.full_name||'').toLowerCase().includes(q) ||
           (s.roll_no||'').toLowerCase().includes(q) ||
           (s.username||'').toLowerCase().includes(q) ||
           (s.email||'').toLowerCase().includes(q) ||
           (s.class_name||'').toLowerCase().includes(q);
  }), [students, search, noRollOnly]);

  const noRollCount = students.filter(s => !s.roll_no).length;

  const onSaved = async (msg) => {
    setSuccess(msg); setShowRoll(false); setShowFlag(false);
    setRollStudent(null); setFlagStudent(null);
    setTimeout(() => setSuccess(''), 5000);
    await load();
    // Refresh selected student
    if (selected) {
      const { data } = await supabase.from('profiles').select('*').eq('id', selected.id).maybeSingle();
      if (data) setSelected(data);
    }
  };

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="students">

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Students</h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>
              {students.length} enrolled
              {noRollCount > 0 && <span style={{marginLeft:10,color:'#F59E0B',fontWeight:700}}>⚠️ {noRollCount} without roll number</span>}
            </p>
          </div>
          <button onClick={load} style={{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>
            <RefreshCw size={14}/> Refresh
          </button>
        </div>

        {success && <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}

        {/* No roll number alert */}
        {noRollCount > 0 && (
          <div style={{background:'#FFF7ED',border:'1.5px solid #FED7AA',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:22}}>⚠️</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:'#92400E',fontSize:14}}>{noRollCount} Student{noRollCount>1?'s':''} Without Roll Number</div>
              <div style={{fontSize:12,color:'#B45309'}}>Students without roll numbers won't be linked to their class, attendance, fees, or library records automatically.</div>
            </div>
            <button onClick={() => setNoRollOnly(v => !v)}
              style={{padding:'7px 14px',borderRadius:8,border:'none',background:noRollOnly?'#F59E0B':'#FEF3C7',color:noRollOnly?'#fff':'#92400E',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
              {noRollOnly ? 'Show All' : 'Show Only'}
            </button>
          </div>
        )}

        {/* Search */}
        <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:12,marginBottom:14,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{position:'relative',flex:1,minWidth:200}}>
            <Search size={14} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
            <input value={searchInput} onChange={e=>hs(e.target.value)}
              placeholder="Search by name, roll no, class, username, email..."
              style={{...INP,paddingLeft:28}}/>
          </div>
          {search && <button onClick={()=>{setSearchInput('');setSearch('');}} style={{padding:'8px 10px',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer'}}><X size={13}/></button>}
          <span style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filtered.length}/{students.length}</span>
        </div>

        {/* Two-column layout: list + detail */}
        <div style={{display:'grid',gridTemplateColumns:selected?'1fr 360px':'1fr',gap:16}}>
          {/* Student Table */}
          <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'auto'}}>
            {loading ? (
              <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>No students found</div>
            ) : (
              <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
                <thead>
                  <tr style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB'}}>
                    {['#','Name','Roll No','Class','Degree','Status','Actions'].map(h=>(
                      <th key={h} style={{padding:'11px 14px',textAlign:'left',fontSize:11,fontWeight:800,color:'#6B7280',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s,i) => {
                    const isFlagged = s.account_status==='flagged';
                    const noRoll    = !s.roll_no;
                    const isSelected= selected?.id===s.id;
                    return (
                      <tr key={s.id}
                        onClick={() => setSelected(isSelected ? null : s)}
                        style={{borderBottom:i<filtered.length-1?'1px solid #F1F5F9':'none',
                          background:isSelected?'#EFF6FF':isFlagged?'#FFF5F5':noRoll?'#FFFBF0':'transparent',
                          cursor:'pointer'}}>
                        <td style={{padding:'10px 14px',fontSize:11,color:'#9CA3AF'}}>{i+1}</td>
                        <td style={{padding:'10px 14px'}}>
                          <div style={{fontWeight:700,color:'#111827',fontSize:13}}>{s.full_name}</div>
                          {s.username&&<div style={{fontSize:10,color:'#9CA3AF'}}>@{s.username}</div>}
                        </td>
                        <td style={{padding:'10px 14px'}}>
                          {s.roll_no ? (
                            <code style={{fontSize:12,fontWeight:800,color:'#4F8EF7',background:'#EFF6FF',padding:'3px 8px',borderRadius:5}}>{s.roll_no}</code>
                          ) : (
                            <button onClick={e=>{e.stopPropagation();setRollStudent(s);setShowRoll(true);}}
                              style={{fontSize:10,fontWeight:700,color:'#F59E0B',background:'#FEF3C7',border:'1px solid #FED7AA',padding:'3px 10px',borderRadius:5,cursor:'pointer'}}>
                              + Set Roll No
                            </button>
                          )}
                        </td>
                        <td style={{padding:'10px 14px',fontSize:12,color:'#374151'}}>{s.class_name||'—'}</td>
                        <td style={{padding:'10px 14px',fontSize:12,color:'#374151'}}>
                          {s.degree||'—'}{s.year_of_study?` Yr${s.year_of_study}`:''}
                        </td>
                        <td style={{padding:'10px 14px'}}>
                          {isFlagged
                            ? <span style={{fontSize:10,fontWeight:800,color:'#EF4444',background:'#FEF2F2',padding:'3px 8px',borderRadius:5}}>🚩 Flagged</span>
                            : <span style={{fontSize:10,fontWeight:700,color:'#059669',background:'#F0FDF4',padding:'3px 8px',borderRadius:5}}>✅ Active</span>}
                        </td>
                        <td style={{padding:'8px 12px'}}>
                          <div style={{display:'flex',gap:5}}>
                            <button onClick={e=>{e.stopPropagation();setRollStudent(s);setShowRoll(true);}}
                              style={{padding:'5px 8px',borderRadius:6,border:'1px solid #BFDBFE',background:'#EFF6FF',cursor:'pointer',fontSize:10,fontWeight:700,color:'#1D4ED8'}}>
                              🔗 Roll
                            </button>
                            <button onClick={e=>{e.stopPropagation();setFlagStudent(s);setShowFlag(true);}}
                              style={{padding:'5px 8px',borderRadius:6,border:`1px solid ${isFlagged?'#BBF7D0':'#FECACA'}`,background:isFlagged?'#F0FDF4':'#FEF2F2',cursor:'pointer'}}>
                              <Flag size={11} color={isFlagged?'#059669':'#EF4444'}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail Panel */}
          {selected && (
            <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',padding:20,alignSelf:'start',position:'sticky',top:16}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                <div style={{fontSize:14,fontWeight:800,color:'#111827'}}>Student Details</div>
                <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18} color="#9CA3AF"/></button>
              </div>

              {/* Avatar */}
              <div style={{textAlign:'center',marginBottom:16}}>
                <div style={{width:56,height:56,borderRadius:'50%',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 8px',color:'#fff',fontSize:22,fontWeight:800}}>
                  {(selected.full_name||'?')[0].toUpperCase()}
                </div>
                <div style={{fontWeight:800,color:'#111827',fontSize:16}}>{selected.full_name}</div>
                {selected.username&&<div style={{fontSize:12,color:'#9CA3AF'}}>@{selected.username}</div>}
              </div>

              {/* Roll number — prominent */}
              <div style={{background:selected.roll_no?'linear-gradient(135deg,#EFF6FF,#F5F3FF)':'#FFF7ED',border:`1px solid ${selected.roll_no?'#BFDBFE':'#FED7AA'}`,borderRadius:12,padding:14,marginBottom:14,textAlign:'center'}}>
                <div style={{fontSize:11,fontWeight:700,color:selected.roll_no?'#1D4ED8':'#92400E',marginBottom:4}}>ROLL NUMBER</div>
                <div style={{fontSize:20,fontWeight:900,color:selected.roll_no?'#4F8EF7':'#F59E0B'}}>
                  {selected.roll_no || 'Not Set'}
                </div>
                <button onClick={()=>{setRollStudent(selected);setShowRoll(true);}}
                  style={{marginTop:8,padding:'6px 16px',borderRadius:8,border:'none',background:selected.roll_no?'#4F8EF7':'#F59E0B',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                  {selected.roll_no ? '✏️ Change Roll No' : '+ Set Roll Number'}
                </button>
              </div>

              {/* Info rows */}
              {[
                ['📧 Email',      selected.email       || '—'],
                ['📱 Phone',      selected.phone       || '—'],
                ['🎓 Degree',     selected.degree      || '—'],
                ['📅 Year',       selected.year_of_study ? `Year ${selected.year_of_study}` : '—'],
                ['🏫 Class',      selected.class_name  || '—'],
                ['🏙 City',       selected.city        || '—'],
                ['📌 Status',     selected.account_status === 'flagged' ? '🚩 Flagged' : '✅ Active'],
                ['📅 Joined',     selected.created_at ? new Date(selected.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'],
              ].map(([k,v]) => (
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid #F1F5F9',fontSize:12}}>
                  <span style={{color:'#6B7280'}}>{k}</span>
                  <span style={{fontWeight:700,color:'#111827',textAlign:'right',maxWidth:'60%'}}>{v}</span>
                </div>
              ))}

              {selected.bio && (
                <div style={{marginTop:12,background:'#F8FAFF',borderRadius:8,padding:10,fontSize:12,color:'#374151',lineHeight:1.5}}>
                  "{selected.bio}"
                </div>
              )}

              <button onClick={()=>{setFlagStudent(selected);setShowFlag(true);}}
                style={{width:'100%',marginTop:14,padding:'10px 0',borderRadius:10,border:`1px solid ${selected.account_status==='flagged'?'#BBF7D0':'#FECACA'}`,background:selected.account_status==='flagged'?'#F0FDF4':'#FEF2F2',color:selected.account_status==='flagged'?'#059669':'#EF4444',fontWeight:700,cursor:'pointer',fontSize:13}}>
                {selected.account_status==='flagged' ? '✅ Unflag Student' : '🚩 Flag Student'}
              </button>
            </div>
          )}
        </div>

        {/* Modals */}
        {showRoll && rollStudent && (
          <SetRollModal key={rollStudent.id} student={rollStudent}
            institutionId={institution?.id}
            onSave={onSaved} onClose={()=>{setShowRoll(false);setRollStudent(null);}}/>
        )}
        {showFlag && flagStudent && (
          <FlagModal student={flagStudent}
            onSave={onSaved} onClose={()=>{setShowFlag(false);setFlagStudent(null);}}/>
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}